import { ParamMap } from '@angular/router';
import { LocalDate } from '../model/local-date';

const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function readEnumParam<TEnum extends Record<string, string | number>>(
  params: ParamMap,
  name: string,
  enumType: TEnum
): TEnum[keyof TEnum] | undefined {
  const raw = params.get(name);
  if (raw === null || raw.trim() === '') return undefined;

  const enumEntries = Object.entries(enumType).filter(([key]) => !/^\d+$/.test(key));
  const namedValue = enumEntries.find(([key]) => toCamelCase(key) === raw);
  if (namedValue) return namedValue[1] as TEnum[keyof TEnum];

  if (/^-?\d+$/.test(raw)) {
    const numericValue = Number(raw);
    if (enumEntries.some(([, value]) => value === numericValue)) return numericValue as TEnum[keyof TEnum];
  }

  const stringValue = enumEntries.find(([, value]) => value === raw)?.[1];
  return stringValue as TEnum[keyof TEnum] | undefined;
}

export function readEnumListParam<TEnum extends Record<string, string | number>>(
  params: ParamMap,
  name: string,
  enumType: TEnum
): Array<TEnum[keyof TEnum]> {
  const raw = params.get(name);
  if (raw === null || raw.trim() === '') return [];
  return raw
    .split(',')
    .map(value => readEnumValue(value.trim(), enumType))
    .filter((value): value is TEnum[keyof TEnum] => value !== undefined);
}

export function readIntegerListParam(params: ParamMap, name: string, allowedValues?: readonly number[]): number[] {
  const raw = params.get(name);
  if (raw === null || raw.trim() === '') return [];
  return raw
    .split(',')
    .map(value => value.trim())
    .filter(value => /^\d+$/.test(value))
    .map(Number)
    .filter(value => !allowedValues || allowedValues.includes(value));
}

export function readIntegerParam(params: ParamMap, name: string, min?: number, max?: number): number | undefined {
  const raw = params.get(name)?.trim();
  if (!raw || !/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  if (!Number.isSafeInteger(value) || (min !== undefined && value < min) || (max !== undefined && value > max)) return undefined;
  return value;
}

export function readBooleanParam(params: ParamMap, name: string): boolean | undefined {
  const raw = params.get(name)?.trim().toLowerCase();
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return undefined;
}

export function readIdParam(params: ParamMap, name: string): string | undefined {
  const raw = params.get(name)?.trim();
  return raw && guidPattern.test(raw) ? raw : undefined;
}

export function readIdListParam(params: ParamMap, name: string): string[] {
  const raw = params.get(name);
  if (raw === null || raw.trim() === '') return [];
  return raw
    .split(',')
    .map(value => value.trim())
    .filter(value => guidPattern.test(value));
}

export function readLocalDateParam(params: ParamMap, name: string): LocalDate | undefined {
  const raw = params.get(name);
  if (raw === null) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return undefined;

  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > LocalDate.daysInMonth(year, month)) return undefined;

  return new LocalDate(year, month, day);
}

export function hasExternalQuery(params: ParamMap, names: readonly string[]): boolean {
  return names.some(name => params.has(name));
}

export function filterStateSignature(state: any): string {
  const normalized = Object.fromEntries(
    Object.keys(state ?? {})
      .sort()
      .map(key => [key, state[key]])
  );
  return JSON.stringify(normalized);
}

export function withIgnoreSaveOnLoad<T extends Record<string, any>>(filter: T, active: boolean): T & { ignoreSaveOnLoad: boolean } {
  return { ...filter, ignoreSaveOnLoad: !!active };
}

function readEnumValue<TEnum extends Record<string, string | number>>(
  raw: string,
  enumType: TEnum
): TEnum[keyof TEnum] | undefined {
  const enumEntries = Object.entries(enumType).filter(([key]) => !/^\d+$/.test(key));
  const namedValue = enumEntries.find(([key]) => toCamelCase(key) === raw);
  if (namedValue) return namedValue[1] as TEnum[keyof TEnum];

  if (/^-?\d+$/.test(raw)) {
    const numericValue = Number(raw);
    if (enumEntries.some(([, value]) => value === numericValue)) return numericValue as TEnum[keyof TEnum];
  }

  return enumEntries.find(([, value]) => value === raw)?.[1] as TEnum[keyof TEnum] | undefined;
}

function toCamelCase(value: string): string {
  return value.length === 0 ? value : value[0].toLowerCase() + value.slice(1);
}
